import { ExpandMore } from "@mui/icons-material";
import type { ChipProps } from "@mui/material";
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import {
  DataGridPremium,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from "@mui/x-data-grid-premium";
import { useCallback, useState } from "react";
import { UserAvatar } from "src/components/UserAvatar";
import { getUserDisplayName } from "src/utils/user";
import { getTrainingRevisionNumber } from "./revision-label";
import {
  formatTrainingMatrixDateTime,
  type TaskProgramUserMatrixAssignment,
  type TaskProgramUserMatrixRow,
  type TrainingMatrixFunctionalGroup,
  type TrainingMatrixStatus,
} from "./training-user-matrix";

type TrainingUserMatrixProps = {
  rows: TaskProgramUserMatrixRow[];
  isLoading: boolean;
  isError: boolean;
  onSelectProgram: (programId: string) => void;
};

type TrainingUserGridRow = TaskProgramUserMatrixRow & {
  id: string;
  traineeName: string;
};

const STATUS_CHIP: Record<
  TrainingMatrixStatus,
  {
    label: string;
    color: ChipProps["color"];
    variant?: ChipProps["variant"];
  }
> = {
  COMPLETED: { label: "Completed", color: "success" },
  PENDING: { label: "Pending", color: "warning" },
  OVERDUE: { label: "Overdue", color: "error" },
  AWAITING_QUIZ_PUBLICATION: {
    label: "Awaiting quiz publication",
    color: "warning",
    variant: "outlined",
  },
  NOT_ISSUED: {
    label: "Not yet issued",
    color: "default",
    variant: "outlined",
  },
};

const PROGRAM_ROW_BACKGROUND = "#f8f9fc";
const ASSIGNMENT_ROW_HEIGHT = 40;
const READ_ONLY_COLUMN_PROPS = {
  align: "left",
  headerAlign: "left",
  sortable: false,
} as const;
const RESPONSIVE_COLUMN_PROPS = {
  ...READ_ONLY_COLUMN_PROPS,
  flex: 1,
} as const;
const LEADING_COLUMN_PROPS = {
  ...RESPONSIVE_COLUMN_PROPS,
  flex: 2,
  minWidth: 280,
} as const;
const ROW_CONTENT_PROPS = {
  direction: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  sx: { minWidth: 0, width: "100%" },
} as const;

const renderLeadingHeader = (label: string) => (
  <Typography variant="overline" color="text.secondary">
    {label}
  </Typography>
);

const renderHeader = (label: string) => (
  <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
    {label}
  </Typography>
);

const getAutoHeight = () => "auto" as const;
const READ_ONLY_GRID_PROPS = {
  autoHeight: true,
  disableVirtualization: process.env.NODE_ENV === "test",
  disableColumnMenu: true,
  disableColumnReorder: true,
  disableRowSelectionOnClick: true,
  hideFooter: true,
  scrollbarSize: 0,
} as const;

const TRAINING_GRID_STYLES = {
  lineHeight: 2,
  fontSize: "0.875rem",
  border: 0,
  ".MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
  },
  ".MuiDataGrid-cell:not(:last-child), .MuiDataGrid-columnHeader:not(:last-child)":
    {
      borderRight: "none",
    },
  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
    py: "9px",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
  },
  ".MuiDataGrid-columnHeader": {
    backgroundColor: "background.paper",
  },
  ".MuiDataGrid-cellContent, .MuiDataGrid-columnHeaderTitle": {
    textOverflow: "clip",
  },
  ".MuiDataGrid-virtualScroller": {
    overflow: "hidden",
  },
  "& .MuiDataGrid-filler": {
    borderBottom: "none",
  },
  ".MuiDataGrid-cell.MuiDataGrid-cellEmpty": {
    border: "none",
  },
  ".MuiDataGrid-container--top [role=row]": {
    backgroundColor: "transparent",
  },
  ".MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within": { outline: 0 },
};

const renderDate = (value: string | null, overdue = false) => {
  if (!value) return null;

  return (
    <Typography
      variant="body2"
      sx={{
        color: overdue ? "#B42318" : "text.primary",
        fontWeight: overdue ? 600 : 400,
        whiteSpace: "nowrap",
      }}
    >
      {new Date(value).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}
    </Typography>
  );
};

const renderFunctionalGroups = (groups: TrainingMatrixFunctionalGroup[]) =>
  groups.length > 0 ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        flexWrap: "wrap",
        gap: 0.5,
        width: "100%",
      }}
    >
      {groups.map((group) => (
        <Tooltip key={group.id} title={group.name} arrow>
          <Chip
            label={group.acronym}
            size="tiny"
            variant="outlined"
            color="primary"
          />
        </Tooltip>
      ))}
    </Box>
  ) : null;

const renderStatusChip = (status: TrainingMatrixStatus) => {
  const chip = STATUS_CHIP[status];
  return (
    <Chip
      className="text-sm"
      label={chip.label}
      color={chip.color}
      variant={chip.variant}
      size="tiny"
    />
  );
};

const ASSIGNMENT_COLUMNS: GridColDef<TaskProgramUserMatrixAssignment>[] = [
  {
    field: "programName",
    ...LEADING_COLUMN_PROPS,
    headerName: "Program",
    renderCell: ({ row }) => (
      <Stack {...ROW_CONTENT_PROPS} spacing={1}>
        <Tooltip title={row.programName} arrow>
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {row.programName}
          </Typography>
        </Tooltip>
        {row.revision != null && (
          <Chip
            label={`R${getTrainingRevisionNumber(row.revision)}`}
            size="tiny"
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        )}
      </Stack>
    ),
  },
  {
    field: "functionalGroups",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Functional groups",
    minWidth: 160,
    renderCell: ({ row }) => renderFunctionalGroups(row.functionalGroups),
  },
  {
    field: "status",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Status",
    minWidth: 120,
    renderCell: ({ row }) =>
      row.status === "NOT_ISSUED" || row.status === "AWAITING_QUIZ_PUBLICATION"
        ? renderStatusChip(row.status)
        : row.revision == null
          ? null
          : renderStatusChip(row.status),
  },
  {
    field: "dueDate",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Next due date",
    minWidth: 140,
    renderCell: ({ row }) => renderDate(row.dueDate, row.status === "OVERDUE"),
  },
  {
    field: "assignedAt",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Assigned at",
    minWidth: 140,
    renderCell: ({ row }) => renderDate(row.assignedAt),
  },
  {
    field: "completedAt",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Completed",
    minWidth: 180,
    renderCell: ({ row }) =>
      row.completedAt ? (
        <Typography
          variant="body2"
          sx={{ color: "text.primary", whiteSpace: "nowrap" }}
        >
          {formatTrainingMatrixDateTime(row.completedAt)}
        </Typography>
      ) : null,
  },
  {
    field: "detailSpacer",
    ...READ_ONLY_COLUMN_PROPS,
    headerName: "",
    width: 52,
  },
];

const USER_COLUMNS: GridColDef<TrainingUserGridRow>[] = [
  {
    field: "trainee",
    ...LEADING_COLUMN_PROPS,
    headerName: "Trainee",
    renderHeader: () => renderLeadingHeader("Trainee"),
    renderCell: ({ row }) => (
      <Stack {...ROW_CONTENT_PROPS} spacing={1.5}>
        <UserAvatar
          userId={row.trainee.id}
          name={row.traineeName}
          size={32}
          fontSize={12}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {row.traineeName}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {row.trainee.email}
          </Typography>
        </Box>
      </Stack>
    ),
  },
  {
    field: "functionalGroups",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Functional groups",
    renderHeader: () => renderHeader("Functional groups"),
    minWidth: 160,
    renderCell: ({ row }) => renderFunctionalGroups(row.functionalGroups),
  },
  {
    field: "status",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Status",
    renderHeader: () => renderHeader("Status"),
    minWidth: 120,
    renderCell: ({ row }) => renderStatusChip(row.status),
  },
  {
    field: "nextDueDate",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Next due date",
    renderHeader: () => renderHeader("Next due date"),
    minWidth: 140,
    renderCell: ({ row }) =>
      renderDate(row.nextDueDate, row.status === "OVERDUE"),
  },
  {
    field: "assignedAt",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Assigned at",
    renderHeader: () => renderHeader("Assigned at"),
    minWidth: 140,
    renderCell: ({ row }) => renderDate(row.assignedAt),
  },
  {
    field: "completedCount",
    ...RESPONSIVE_COLUMN_PROPS,
    headerName: "Completed",
    renderHeader: () => renderHeader("Completed"),
    minWidth: 180,
    renderCell: ({ row }) =>
      row.totalCount === 0 ? null : (
        <Stack {...ROW_CONTENT_PROPS} spacing={1}>
          <LinearProgress
            variant="determinate"
            value={(row.completedCount / row.totalCount) * 100}
            color={
              row.status === "COMPLETED"
                ? "success"
                : row.status === "OVERDUE"
                  ? "warning"
                  : "primary"
            }
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: 120,
              height: 5,
              borderRadius: 999,
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            {row.completedCount}/{row.totalCount}
          </Typography>
        </Stack>
      ),
  },
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
    ...READ_ONLY_COLUMN_PROPS,
    width: 52,
    renderCell: (
      params: GridRenderCellParams<TrainingUserGridRow, boolean>
    ) => {
      const isExpanded = Boolean(params.value);
      return (
        <IconButton
          size="small"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? "Collapse" : "Expand"} training assignments for ${params.row.traineeName}`}
        >
          <ExpandMore
            sx={{
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 150ms ease-in-out",
            }}
          />
        </IconButton>
      );
    },
  },
];

const getDetailPanelHeight = ({ row }: GridRowParams<TrainingUserGridRow>) =>
  row.assignments.length * ASSIGNMENT_ROW_HEIGHT;

export const TrainingUserMatrix = ({
  rows,
  isLoading,
  isError,
  onSelectProgram,
}: TrainingUserMatrixProps) => {
  const [expandedTraineeIds, setExpandedTraineeIds] = useState<GridRowId[]>([]);
  const renderDetailPanel = useCallback(
    ({ row }: GridRowParams<TrainingUserGridRow>) => (
      <DataGridPremium
        aria-label={`Training assignments for ${row.traineeName}`}
        rows={row.assignments}
        columns={ASSIGNMENT_COLUMNS}
        getRowId={(assignment) => assignment.taskStateId}
        rowHeight={ASSIGNMENT_ROW_HEIGHT}
        columnHeaderHeight={0}
        onRowClick={({ row: assignment }) =>
          onSelectProgram(assignment.programId)
        }
        {...READ_ONLY_GRID_PROPS}
        sx={{
          ...TRAINING_GRID_STYLES,
          backgroundColor: PROGRAM_ROW_BACKGROUND,
          ".MuiDataGrid-row": {
            backgroundColor: PROGRAM_ROW_BACKGROUND,
            cursor: "pointer",
            "&:hover": { backgroundColor: "action.hover" },
          },
        }}
      />
    ),
    [onSelectProgram]
  );

  if (isLoading) {
    return (
      <Stack spacing={1.5} sx={{ px: 2, py: 2 }}>
        <Skeleton variant="rounded" height={52} />
        <Skeleton variant="rounded" height={52} />
        <Skeleton variant="rounded" height={52} />
      </Stack>
    );
  }

  if (isError || rows.length === 0) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography
          variant="body2"
          color={isError ? "error" : "text.secondary"}
        >
          {isError
            ? "Could not load trainee training assignments. Please try again."
            : "No trainee assignments in this training scope yet."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <DataGridPremium
        aria-label="Training matrix by user"
        rows={rows.map((row) => ({
          ...row,
          id: row.trainee.id,
          traineeName: getUserDisplayName(row.trainee),
        }))}
        columns={USER_COLUMNS}
        columnHeaderHeight={44}
        getRowHeight={getAutoHeight}
        getEstimatedRowHeight={() => 57.5}
        getDetailPanelContent={renderDetailPanel}
        getDetailPanelHeight={getDetailPanelHeight}
        detailPanelExpandedRowIds={expandedTraineeIds}
        onDetailPanelExpandedRowIdsChange={setExpandedTraineeIds}
        onCellClick={(params) => {
          if (params.field !== GRID_DETAIL_PANEL_TOGGLE_FIELD) {
            setExpandedTraineeIds((current) =>
              current.includes(params.id)
                ? current.filter((id) => id !== params.id)
                : [...current, params.id]
            );
          }
        }}
        {...READ_ONLY_GRID_PROPS}
        sx={{
          ...TRAINING_GRID_STYLES,
          minWidth: 1080,
        }}
      />
    </Box>
  );
};
